import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import * as React from 'react';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { confirmCode } from "../offchain-logic/services";
import { checkUserStatus } from "../services/authService";
import { loginWithCookies } from "../services/authService";
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CentralizedDialog from './component/CentralizedDialog';
import ForgotPasswordFlow from './ForgotPassword';
import PaymentPageDialog from './component/PaymentPageDialog';
import TermsAndPaymentDialog from './TermsAndPaymentGate';

const COLOR = "#20679F"

function Copyright(props: any) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Copyright '}
            &copy; {' '}
            <Link color="inherit" href="#">
                MAMADOC
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

export default function Login(props: any) {
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>(""); 
    const [code, setCode] = useState<string>("");
    const [loginVisible, setLoginVisible] = useState<Boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const navigate = useNavigate();
    
    // Stato per il dialogo di Forgot Password
    const [isForgotPassOpen, setIsForgotPassOpen] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    
    // Stato per mostrare i termini.
    const [showTermsGate, setShowTermsGate] = useState(false); 
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleOpenForgotPass = (e: React.MouseEvent) => {
        e.preventDefault(); // Previene l'azione di navigazione del link
        setIsForgotPassOpen(true);
    };
    const handleCloseForgotPass = () => {
        setIsForgotPassOpen(false);
    };
    const handleClosePayment = () => setShowPayment(false);

    useEffect(() => {
        // Pulizia sessionStorage al mount del componente login
        // I token sono gestiti dai cookie HttpOnly, qui puliamo solo i dati utente
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('IdToken');
        sessionStorage.removeItem('AccessToken');
    }, []);

    // Funzione che viene chiamata quando il pagamento va a buon fine
    const handlePaymentSuccess = () => {
        props.handleOpenToast("Pagamento riuscito! Account attivo.", 'success');
        setShowPayment(false); // Nasconde il modulo di pagamento
        
        // **IMPORTANTE**: Ricarica lo stato dell'utente o reindirizza
        // checkStatusUserAPI(); 
        navigate("/dashboard"); // Reindirizza alla Dashboard dopo login
    };



    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (isSubmitting) return;
        if (email == null || password === "") {
            console.error("Email e password sono campi obbligatori");
            props.handleOpenToast("Email e password sono campi obbligatori", 'error');
            return;
        }
        
        const loginStartedAt = performance.now();
        setIsSubmitting(true);
        try {
            // Usa authService con httpOnly cookies
            const response = await loginWithCookies(email, password);
            
            console.log("Login con cookies riuscito:", response);
            
            // I token sono ora nei cookies httpOnly, salva solo user info
            if (response.success && response.user) {
                props.onLogin(null, email, response.user);
                
                // Gli Admin bypassano il controllo subscription
                const groups = response.user?.['cognito:groups'] || [];
                const isAdmin = groups.includes('Admin');
                
                if (isAdmin) {
                    console.log('🔑 Admin login detected - bypassing subscription check');
                    setShowPayment(false);
                    navigate("/admin-dashboard");
                    return;
                }
                
                // Verifica stato utente per abbonamento (solo utenti normali)
                try {
                    const statusRes = await checkUserStatus();
                    console.log("checkUserStatus res:", statusRes);
                    
                    if (statusRes.hasAccess === false) {
                        setShowTermsGate(true);
                        props.handleOpenToast("Abbonamento non attivo. Per procedere devi accettare prima i 'Termini e le Condizioni' e poi procedere al pagamento.", 'warning');
                    } else {
                        setShowPayment(false);
                        navigate("/dashboard");
                    }
                } catch (statusError: any) {
                    console.error("Errore verifica status:", statusError);
                    props.handleOpenToast("Si è verificato un problema verificando lo stato dell'account.", 'error');
                    props.onLogout();
                }
            }
        } catch (error: any) {
            console.error("Errore login:", error);
            
            if (error.name === 'UserNotConfirmedException') {
                setLoginVisible(false);
            } else {
                props.handleOpenToast("Si è verificato un problema: " + (error.message || "errore sconosciuto"), 'error');
            }
        } finally {
            console.log(`⏱️ [LOGIN] Durata completa accesso: ${Math.round(performance.now() - loginStartedAt)} ms`);
            setIsSubmitting(false);
        }
    }

    // Funzione da passare al componente TermsAndPaymentGate
    const handleTermsAccepted = () => {
        setShowTermsGate(false); // Nasconde il gate dei termini
        setShowPayment(true);  // Mostra il modulo di pagamento
        props.handleOpenToast("Termini accettati! Procedi al pagamento.", 'success');
        // Resetta il ref per la prossima volta

    };

    const handleCloseTermsGate = () => {
        setShowTermsGate(false);
        // Rimuove il ref quando si chiude il modale

        // Qui potresti anche forzare il logout se non si accetta, a seconda delle regole
        props.onLogout(); 
    }

    const handleInsertCodeSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (code === "") {
            setErrorMessage("Inserire il codice");
            return;
        }

        confirmCode({
            'ConfirmationCode': code,
            'Username': email,
        }, (err, res) => {
            if(err !== null){
                console.error(err);
                if(err.code === "InvalidParameterException"){
                    setErrorMessage("Verifica la correttezza dei valori dei campi inseriti e riprova.");
                } else {
                    setErrorMessage("Si è verificato un problema, riprova più tardi.");
                }
                return;
            }
            setLoginVisible(true);
        });
    }

    const handleCloseError = (event: React.SyntheticEvent<unknown>, reason?: string) => {
        setErrorMessage(undefined);
    }

    return (
        <>
        <Grid container component="main" sx={{ height: '100vh' }}>
            <CssBaseline />
            { loginVisible ? getLoginContent() : getInsertCodeContent() }

            <Grid
                item
                xs={false}
                sm={3}
                md={5}
                sx={{
                    backgroundColor: COLOR,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: '100%' }}
                >
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                        <Typography variant="h3" align="center" color={'white'} fontWeight="bold" mb={3} >
                            Non hai un account?
                        </Typography>
                        <Typography variant="body1" color={'white'} align="center"
                            sx={{ width: '60%', margin: '0 auto' }}>
                            Inserisci i tuoi dati e creane uno
                        </Typography>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{
                                borderRadius: '50px',
                                mt: 3,
                                mb: 2,
                                backgroundColor: 'transparent',
                                border: '2px solid #FFFFFF',
                                width: '15em',
                                color: '#FFFFFF',

                            }}
                            onClick={() => navigate("/enroll")}
                        >
                            <Typography variant="h6" align="center" textAlign="center">
                                REGISTRATI
                            </Typography>
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
        <Dialog disableEscapeKeyDown open={errorMessage !== undefined} onClose={handleCloseError}>
            <DialogTitle>ERRORE</DialogTitle>
            <DialogContent>
                <span dangerouslySetInnerHTML={{__html: errorMessage ?? ""}}></span>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseError} style={{ color: "#8B6F47", fontWeight: "bolder" }} >Ok</Button>
            </DialogActions>
        </Dialog>

        <PaymentPageDialog
            open={showPayment}
            handleClose={handleClosePayment}
            handlePaymentSuccess={handlePaymentSuccess}
        />
    </>);

    function getLoginContent() {
        return (
            <Grid item
                xs={12} sm={9} md={7} component={Paper} elevation={6} square
                sx={{ position: 'relative' }}
            >
                <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: '100%', pb: 8 }}
                >
                    <div style={{
                        width: '90%',
                        maxWidth: '500px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px'
                    }}>
                        <img 
                            src="/logo-app-transparent.png"
                            alt="Logo" 
                            style={{
                                width: '100%',
                                height: 'auto',
                                display: 'block'
                            }}
                        />
                    </div>
                    <Box id="loginForm" component="form" noValidate sx={{ mt: 5 }} onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Indirizzo Email"
                            name="email"
                            onChange={e => setEmail(e.target.value)}
                            value={email}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            onChange={e => setPassword(e.target.value)}
                            value={password}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}mt={5}>
                            <Link href="#" variant="body2" onClick={handleOpenForgotPass}>
                                Hai dimenticato la password?
                            </Link>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{ borderRadius: '50px', mt: 3, mb: 2, backgroundColor: 'primary' }}
                        >
                                {isSubmitting ? <CircularProgress size={22} color="inherit" /> : 'ACCEDI'}
                        </Button>
                                        </Box>
                        {/* 2. Il Dialogo di Forgot Password (Non richiede ref per lo scroll) */}
                        <CentralizedDialog
                            open={isForgotPassOpen}
                            onClose={handleCloseForgotPass}
                            title="Reset Password"
                            maxWidth="xs" // Generalmente il reset è piccolo
                            
                            // Passiamo il componente del flusso di reset come Children
                            children={<ForgotPasswordFlow 
                                        key={isForgotPassOpen ? 'forgot-password-open' : 'forgot-password-closed'}
                                        initialUsername={email}
                                        onBackToLogin={handleCloseForgotPass}
                                        />}
                            
                        />



                        <>
                            <TermsAndPaymentDialog
                                open={showTermsGate}
                                onClose={handleCloseTermsGate}
                                onAccept={handleTermsAccepted}
                            />
                        </>

                    </Box>
                    
                    <Copyright sx={{ position: 'absolute', bottom: 20, width: '100%' }} />
                </Grid>
            </Grid>
        );
    }

    function getInsertCodeContent() {
        return (
            <Grid item
                xs={12} sm={9} md={7} component={Paper} elevation={6} square
            >
                <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: '100%' }}
                >
                    <Typography component="h1" variant="h4"  color={COLOR}>
                        Inserisci il codice che ti è stato inviato
                    </Typography>
                    <Box id="insertCodeForm" component="form" noValidate onSubmit={handleInsertCodeSubmit} sx={{ mt: 5 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="code"
                            type='number'
                            label="Codice"
                            name="code"
                            onChange={e => setCode(e.target.value)}
                            value={code}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} mt={5}>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{ borderRadius: '50px', mt: 3, mb: 2, backgroundColor: 'primary' }}
                            >
                                CONFERMA
                            </Button>
                        </Box>
                        <Copyright sx={{ mt: 5 }} />
                    </Box>
                </Grid>
            </Grid>
        );
    }
    
}